import {FormModel} from "../../../../../../models/forms/form.model";
import {MaturePassengerModel} from "../../../passenger/mature-passenger.model";
import {IPsoAgreementCommonFormFields, IPsoAgreementCommonPersistedFields} from "./pso-agreement.view-mode.interface";
import {Debounce} from "../../../../../../utils/debounce";
import {TimeSpan} from "../../../../../../types/time-span";
import {IFormField} from "../../../../../../models/forms/form-field.interface";

export abstract class PsoAgreementModelBase<TFields extends IPsoAgreementCommonFormFields> extends FormModel<TFields>{
    constructor(protected readonly passenger: MaturePassengerModel) {
        super(passenger.services);
        this.declarationDate = this.services.time.currentDate;
    }

    declarationDate: Date;

    protected abstract _persistFields(): void;
    protected abstract _resetConfirmation(): void;
    protected abstract isDeclarationDownloaded(): boolean;

    isFormReadOnly(): boolean {
        return this.isDeclarationDownloaded() && this.services.configuration.isProductionLike;
    }

    get passengerKey(): string {
        return this.passenger.passengerKey;
    }

    get passengerFullName(): string {
        return this.passenger.getFullName();
    }

    get passengerFirstName(): string {
        return this.passenger.fields.firstName.value ?? "";
    }

    get passengerLastName(): string {
        return this.passenger.fields.lastName.value ?? "";
    }

    get marketNameTranslation(): string {
        return this.passenger.booking.pso.psoMarket?.translations?.getMarketNameTranslation() ?? "";
    }


    private _persistFieldsDebounce = new Debounce(TimeSpan.fromMilliseconds(300));
    protected _onFieldChanged(field: IFormField) {
        super._onFieldChanged(field);
        this._resetConfirmation();
        this._persistFieldsDebounce.execute(() => this._persistFields());
    }

    protected _createCommonFields(commonPersistedFields: IPsoAgreementCommonPersistedFields | null) {
        const language = this.services.language;
        return {

            dateOfBirth: this._createField<Date>({
                fieldName: () => language.translate('Date of birth'),
                initialValue: () => this.services.time.tryConvertToDate(commonPersistedFields?.dateOfBirth),
                defaultValue: this.passenger.fields.dateOfBirth.value,
            }),
            birthLocation: this._createField<string>({
                fieldName: () => language.translate('Born in'),
                initialValue: () => commonPersistedFields?.birthLocation,
                maxLength: 100
            }),

            residenceLocation: this._createField<string>({
                fieldName: () => language.translate('Resident in'),
                initialValue: () => commonPersistedFields?.residenceLocation,
                maxLength: 100
            }),

            fiscalCode: this._createField<string>({
                fieldName: () => language.translate('Fiscal Code'),
                initialValue: () => commonPersistedFields?.fiscalCode,
                maxLength: 50
            }),

            phoneNumber: this._createPhoneField({
                fieldName: () => language.translate('Phone/mobile'),
                initialValue: () => commonPersistedFields?.phoneNumber,
                defaultValue: this.passenger.fields.phoneNumber.value,
                maxLength: 20
            }),

            phoneNumberCountryCode: this._createField<string>({
                fieldName: () => language.translate('Country'),
                isReadOnly: () => true,
                isHidden: () => true,
                isRequired: false,
                defaultValue: this.services.configuration.defaultCountryCode,
                initialValue: () => this.services.configuration.defaultCountryCode
            }),

            email: this._createField<string>({
                fieldName: () => language.translate('Email Address'),
                initialValue: () => commonPersistedFields?.email,
                defaultValue: this.passenger.fields.emailAddress.value,
                maxLength: 100
            }),
        }
    }

    protected _persistCommonFields(): IPsoAgreementCommonPersistedFields {
        return {
            phoneNumberCountryCode: this.fields.phoneNumberCountryCode.value,
            dateOfBirth: this.fields.dateOfBirth.value && this.services.time.formatBirthDate(this.fields.dateOfBirth.value),
            birthLocation: this.fields.birthLocation.value,
            residenceLocation: this.fields.residenceLocation.value,
            fiscalCode: this.fields.fiscalCode.value,
            phoneNumber: this.fields.phoneNumber.value,
            email: this.fields.email.value,
        }

    }

}